import { useState } from "react";
import { api, API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Download, CheckCircle2, AlertTriangle, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic CSV importer dialog. Props:
 *  - open, onClose
 *  - title (e.g., "Import customers")
 *  - templateUrl (e.g., "/customers/import/template")
 *  - previewUrl  (e.g., "/customers/import/preview")
 *  - commitUrl   (e.g., "/customers/import")
 *  - onImported  (callback after successful commit)
 *  - testIdPrefix
 */
export default function CsvImportDialog({
    open, onClose, title, templateUrl, previewUrl, commitUrl, onImported, testIdPrefix = "csv-import",
}) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [committing, setCommitting] = useState(false);

    const downloadTemplate = async () => {
        const token = localStorage.getItem("vyastra_token");
        const res = await fetch(`${API}${templateUrl}`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob); a.download = title.toLowerCase().replace(/\s+/g, "_") + "_template.csv";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(a.href);
    };

    const runPreview = async () => {
        if (!file) return;
        setLoading(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post(previewUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Preview failed");
        } finally { setLoading(false); }
    };

    const commit = async () => {
        if (!file) return;
        setCommitting(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post(commitUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${data.inserted} inserted, ${data.skipped} skipped`);
            onImported?.();
            handleClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed");
        } finally { setCommitting(false); }
    };

    const handleClose = () => {
        setFile(null); setPreview(null);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="max-w-2xl" data-testid={`${testIdPrefix}-dialog`}>
                <DialogHeader>
                    <div className="text-[10px] font-semibold tracking-[0.18em] text-[#a68a56]">CSV BULK IMPORT</div>
                    <DialogTitle className="text-xl">{title}</DialogTitle>
                    <p className="text-[12.5px] text-muted-foreground">Download the template, fill it in, then upload — we'll validate every row before posting anything.</p>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Step 1: template */}
                    <div className="border border-border rounded-md p-3 bg-[#fafaf7] flex items-center justify-between">
                        <div>
                            <div className="text-[12px] font-semibold">Step 1 — Get template</div>
                            <div className="text-[11.5px] text-muted-foreground">CSV with all supported columns + a sample row.</div>
                        </div>
                        <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid={`${testIdPrefix}-template`}>
                            <Download className="w-3.5 h-3.5 mr-1" />Download template
                        </Button>
                    </div>

                    {/* Step 2: upload */}
                    <div className="border border-border rounded-md p-3">
                        <div className="text-[12px] font-semibold mb-2">Step 2 — Upload your filled CSV</div>
                        <div className="flex gap-2">
                            <input
                                type="file"
                                accept=".csv,text/csv"
                                onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                                className="text-[12px] flex-1 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#13192e] file:text-white file:cursor-pointer"
                                data-testid={`${testIdPrefix}-file`}
                            />
                            <Button onClick={runPreview} disabled={!file || loading} data-testid={`${testIdPrefix}-preview-btn`}>
                                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                                Validate
                            </Button>
                        </div>
                    </div>

                    {/* Step 3: preview */}
                    {preview && (
                        <div className="border border-border rounded-md p-3 space-y-3">
                            <div className="text-[12px] font-semibold">Step 3 — Validation result</div>
                            <div className="grid grid-cols-3 gap-2">
                                <Stat label="Total rows" value={preview.total_rows} />
                                <Stat label="Valid" value={preview.valid_count} accent="positive" />
                                <Stat label="Errors" value={preview.error_count} accent={preview.error_count > 0 ? "danger" : ""} />
                            </div>
                            {preview.issues?.length > 0 && (
                                <div className="border border-border rounded-md max-h-[140px] overflow-y-auto divide-y divide-border">
                                    {preview.issues.slice(0, 30).map((iss, i) => (
                                        <div key={i} className="px-3 py-1.5 text-[11.5px] flex items-start gap-2">
                                            <AlertTriangle className="w-3.5 h-3.5 text-[#c97a3a] mt-0.5 shrink-0" />
                                            <span>{iss}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {preview.preview?.length > 0 && (
                                <details className="text-[11.5px]">
                                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Preview first {preview.preview.length} valid rows</summary>
                                    <pre className="mt-2 bg-[#0f1424] text-[#e5e7eb] rounded-md p-3 max-h-[200px] overflow-auto font-mono text-[10.5px]">
{JSON.stringify(preview.preview, null, 2)}
                                    </pre>
                                </details>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>Cancel</Button>
                    <Button onClick={commit} disabled={!preview || preview.valid_count === 0 || committing} data-testid={`${testIdPrefix}-commit`}>
                        {committing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Importing…</> : <><Upload className="w-4 h-4 mr-1.5" />Import {preview?.valid_count || 0} rows</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Stat({ label, value, accent }) {
    const color = accent === "positive" ? "text-[#1f6c47]" : accent === "danger" ? "text-[#b4233a]" : "text-foreground";
    return (
        <div className="border border-border rounded-md p-2.5">
            <div className="text-[10.5px] tracking-[0.14em] font-semibold text-muted-foreground">{label.toUpperCase()}</div>
            <div className={`text-base font-semibold ${color}`}>{value}</div>
        </div>
    );
}
